// ────────────────────────────────────────────────────────────────────────────
// A template's conditions, put into words — and the cargo definitions they
// reach — against the organisation's real cargo definitions and service tags.
//
// Explanation only. WHICH checkpoints an order gets is decided by the server
// (POST tripCheckpointTemplates/preview, and later the copy sent to a driver),
// so there is one implementation of the assembly. The "reaches" line repeats
// the matching rule (any one within a row, every row used) for definitions
// alone, which is simple enough to keep in step.
// ────────────────────────────────────────────────────────────────────────────
import {
  getCargoTypeLabel,
  type CargoDefinition,
  type CargoTypeValue,
  type ServiceTag,
} from '@my/tms-api';
import {
  PROPERTY_LABEL,
  PROPERTY_PHRASE,
  type AppliesWhen,
  type CargoProperty,
  type CheckpointTemplate,
  type Role,
} from './model';

/** What a cargo definition offers to the conditions. */
export interface CatalogueCargoDefinition {
  id: string;
  type: CargoTypeValue;
  name: string;
  tagIds: string[];
  properties: CargoProperty[];
}

export interface Catalogue {
  cargoDefinitions: CatalogueCargoDefinition[];
  serviceTags: { id: string; name: string }[];
  /** Only the cargo types the organisation actually has definitions for. */
  cargoTypesInUse: CargoTypeValue[];
}

/**
 * The "Cargo has" facts of a definition — the same reading the server makes
 * of `propertyGroupsJson`: a group that is present is a property it has, and
 * Products is the definition's own switch.
 */
function propertiesOf(def: CargoDefinition): CargoProperty[] {
  let groups: Record<string, unknown> = {};
  try {
    const parsed: unknown = def.propertyGroupsJson ? JSON.parse(def.propertyGroupsJson) : null;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) groups = parsed as Record<string, unknown>;
  } catch {
    // An unreadable document has no groups.
  }
  const has = (key: string) => groups[key] !== undefined && groups[key] !== null;
  const out: CargoProperty[] = [];
  if (has('temperature')) out.push('temperature');
  if (has('bic')) out.push('bic');
  if (has('compartments')) out.push('compartments');
  if (def.hasProducts) out.push('products');
  if (has('compliance')) out.push('compliance');
  return out;
}

export function buildCatalogue(definitions: CargoDefinition[], tags: ServiceTag[]): Catalogue {
  const cargoDefinitions = definitions
    .filter((d) => d.status === 'Active')
    .map((d) => ({
      id: d.id,
      type: d.cargoType,
      name: d.displayName,
      tagIds: [...d.serviceTagIds],
      properties: propertiesOf(d),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return {
    cargoDefinitions,
    serviceTags: tags
      .filter((t) => t.status === 'Active')
      .map((t) => ({ id: t.id, name: t.name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    cargoTypesInUse: [...new Set(cargoDefinitions.map((d) => d.type))],
  };
}

const either = (xs: string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`;

const definitionName = (cat: Catalogue, id: string) =>
  cat.cargoDefinitions.find((d) => d.id === id)?.name ?? 'a removed cargo definition';

const tagName = (cat: Catalogue, id: string) => cat.serviceTags.find((t) => t.id === id)?.name ?? 'a removed tag';

/** The conditions as one sentence — so "and" versus "or" is never a guess. */
export function describeAppliesWhen(a: AppliesWhen, cat: Catalogue): string {
  const parts: string[] = [];
  if (a.cargoTypes.length) parts.push(`the cargo type is ${either(a.cargoTypes.map(getCargoTypeLabel))}`);
  if (a.cargoDefinitionIds.length)
    parts.push(`the cargo is ${either(a.cargoDefinitionIds.map((id) => definitionName(cat, id)))}`);
  if (a.serviceTagIds.length) parts.push(`the order is tagged ${either(a.serviceTagIds.map((id) => tagName(cat, id)))}`);
  if (a.properties.length) parts.push(`the cargo ${either(a.properties.map((p) => PROPERTY_PHRASE[p]))}`);
  return parts.length === 0 ? 'Switches on for every order.' : `Switches on when ${parts.join(' and ')}.`;
}

/** Short form for the template list. */
export function summariseAppliesWhen(a: AppliesWhen, cat: Catalogue): string {
  const bits = [
    ...a.cargoTypes.map(getCargoTypeLabel),
    ...a.cargoDefinitionIds.map((id) => definitionName(cat, id)),
    ...a.serviceTagIds.map((id) => `#${tagName(cat, id)}`),
    ...a.properties.map((p) => PROPERTY_LABEL[p]),
  ];
  return bits.length ? bits.join(' · ') : 'Every order';
}

/**
 * The cargo definitions a template reaches on their own — the "per cargo"
 * view: checks are written once, on the template, and the cargo's tags and
 * properties decide where they land. (An order can still add a tag of its own
 * and be reached that way.)
 */
export function definitionsReached(a: AppliesWhen, cat: Catalogue): CatalogueCargoDefinition[] {
  return cat.cargoDefinitions.filter(
    (d) =>
      (!a.cargoTypes.length || a.cargoTypes.includes(d.type)) &&
      (!a.cargoDefinitionIds.length || a.cargoDefinitionIds.includes(d.id)) &&
      (!a.serviceTagIds.length || a.serviceTagIds.some((t) => d.tagIds.includes(t))) &&
      (!a.properties.length || a.properties.some((p) => d.properties.includes(p))),
  );
}

/** Checkpoints per role in one template, for the list. */
export function roleCounts(t: CheckpointTemplate): Record<Role, number> {
  const counts: Record<Role, number> = { planner: 0, driver: 0, sub: 0 };
  for (const s of t.sections) for (const c of s.checkpoints) for (const r of c.roles) counts[r]++;
  return counts;
}
